// "Watch a quick ad to unlock your next style" gate dialog.
// Shown to free Android users on their 2nd and 3rd generation.
// On rewarded callback → resolves the awaiting promise with `true`.
// On dismiss / failure → resolves `false`.
import { useState } from "react";
import { Loader2, PlayCircle, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { showRewardedAd } from "@/lib/native-ads";
import { useAuth } from "@/contexts/AuthContext";
import { tapLight, notifySuccess } from "@/lib/haptics";

interface Props {
  open: boolean;
  /** 1-based: which generation this is (2 or 3). */
  generationNumber: number;
  onResolve: (ok: boolean) => void;
}

export const AdGateDialog = ({ open, generationNumber, onResolve }: Props) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [adFailed, setAdFailed] = useState(false);

  const handleWatch = async () => {
    if (!user) { onResolve(false); return; }
    setBusy(true);
    setAdFailed(false);
    void tapLight();
    try {
      const result = await showRewardedAd({ userId: user.id });
      if (result.rewarded) {
        void notifySuccess();
        onResolve(true);
      } else {
        // No fill / dismissed early. Let user retry or skip.
        setAdFailed(true);
      }
    } catch {
      setAdFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    if (busy) return;
    onResolve(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleCancel();
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Sparkles className="h-7 w-7" />
          </div>
          <DialogTitle className="text-center">Unlock your free try-on</DialogTitle>
          <DialogDescription className="text-center">
            Watch a quick ad to use your free credit.
            <br />
            <span className="text-xs opacity-80">
              Free trial · your 1 lifetime credit
            </span>
          </DialogDescription>
        </DialogHeader>

        {adFailed && (
          <p className="rounded-md bg-amber-500/10 px-3 py-2 text-center text-xs text-amber-700 dark:text-amber-300">
            No ad available right now. Please try again in a moment — watching an ad is required to use this free credit.
          </p>
        )}

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button
            onClick={handleWatch}
            disabled={busy}
            className="w-full gap-2"
            size="lg"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="h-5 w-5" />
            )}
            {busy ? "Loading ad…" : adFailed ? "Try ad again" : "Watch ad to continue"}
          </Button>
          <Button
            variant="ghost"
            onClick={handleCancel}
            disabled={busy}
            className="w-full"
          >
            Maybe later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
